import React from 'react';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ResponsiveBackgroundImageContainer } from '../../components';

interface AuthLayoutProps {
  children: React.ReactNode;
  marketplaceName: string;
  /** When set, renders Login/Sign Up tabs. Omit for standalone pages (recovery, reset, verify). */
  tab?: 'login' | 'signup';
  userType?: string | null;
  /** Optional heading shown below marketplace name (used on non-tab pages). */
  heading?: string;
  /** Optional description below the heading. */
  description?: string;
  /** Optional icon element rendered above the heading. */
  icon?: React.ReactNode;
  /** Brand image from config.branding.brandImage (string URL or image asset). */
  brandImage?: any;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  marketplaceName,
  tab,
  userType,
  heading,
  description,
  icon,
  brandImage,
}) => {
  const history = useHistory();
  const intl = useIntl();

  const handleTabChange = (value: string) => {
    const stateMaybe = userType ? { userType } : {};
    if (value === 'login') {
      history.push('/login', stateMaybe);
    } else {
      const path = userType ? `/signup/${userType}` : '/signup';
      history.push(path, stateMaybe);
    }
  };

  const header = (
    <>
      <h1 className="text-center text-2xl font-semibold tracking-tight">
        {marketplaceName}
      </h1>

      {tab ? (
        <Tabs value={tab} onValueChange={handleTabChange} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signup">
              <FormattedMessage id="AuthenticationPage.signupLinkText" />
            </TabsTrigger>
            <TabsTrigger value="login">
              <FormattedMessage id="AuthenticationPage.loginLinkText" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      ) : (
        <div className="mt-4 flex flex-col items-center space-y-2">
          {icon ? <div className="text-muted-foreground">{icon}</div> : null}
          {heading ? (
            <h2 className="text-center text-lg font-medium">{heading}</h2>
          ) : null}
          {description ? (
            <p className="text-center text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
      )}
    </>
  );

  const contentMinHeight = tab ? "md:min-h-[340px]" : undefined;

  // Mobile: always a clean flat layout, no card chrome, no background image
  const mobileLayout = (
    <div className="md:hidden w-full max-w-md mx-auto px-6 py-8 space-y-4">
      <div className="flex flex-col space-y-1.5">{header}</div>
      {children}
    </div>
  );

  // Desktop card
  const desktopCard = (
    <Card className="hidden md:block w-full max-w-md">
      <div className="flex flex-col space-y-1.5 p-6 pb-2">{header}</div>
      <CardContent className={contentMinHeight}>{children}</CardContent>
    </Card>
  );

  // Desktop: brand image background with overlay, or plain
  const desktopLayout = brandImage ? (
    <ResponsiveBackgroundImageContainer
      className="hidden md:block"
      image={brandImage}
      sizes="100%"
      useOverlay
    >
      <div className="flex flex-1 items-center justify-center px-4 py-6 relative z-10">
        {desktopCard}
      </div>
    </ResponsiveBackgroundImageContainer>
  ) : (
    <div className="hidden md:flex flex-1 items-center justify-center px-4 py-6">
      {desktopCard}
    </div>
  );

  return (
    <>
      {mobileLayout}
      {desktopLayout}
    </>
  );
};

export default AuthLayout;
